import React, { useState, useEffect } from 'react';
import { Confirmation, Table, useToasts, Button, Tooltip } from 'freemium-ui';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { get, isNaN, isNil, uniq } from 'lodash';
import { History } from 'history';
import qs from 'query-string';
import Skeleton from 'react-loading-skeleton';
import { Center, ConfirmationText, DisplayBox } from 'shared/globals';
import {
  AllUsersType,
  CollectorsResponseType,
  CollectorType,
  ConfirmationType,
  GoToRouteType,
  HostConfigType,
  SetBreadcrumbDataType,
  SurveysResponseType,
  SurveyTemplateDescriptionFormType,
  SurveyType,
  UserType,
} from 'shared/types';
import { Flex } from '../view/components/response-filter/style';
import Icons from 'components/icons';
import { Container, InfoComponent, SurveyState, Tag } from './style';
import {
  getCollectorsInfo,
  useDeleteSurvey,
  useGetAllActiveSurveysCount,
  useGetAllSurveys,
  useGetAllSurveysCount,
} from './hooks';
import {
  SurveyChangeStateType,
  useGetSurvey,
  useSaveSurvey,
  useSurveyStateChange,
  useUserInfo,
} from '../action/hooks';
import {
  getSurveyPayload,
  getSurveyStateNameFromState,
  useQueryParams,
} from '../action/utils';
import { useCollectorStateChange } from '../action/collector/hooks';
import ListHeader, { ListEmptyState } from './components/list-header';
import {
  getOptions,
  getActionButtonTextByState,
  getConsolidatedState,
  getSurveyFrequency,
  ChannelCountType,
  getConfiguredCollectors,
} from './utils';
import { AVAILABLE_SURVEY_COLLECTOR_TYPES } from '../action/collector/constants';
import {
  CollectorStateChangeType,
  SurveyCollectorType,
} from '../action/collector/types';
import { useAccountInfo, useGetAccountUsers } from 'shared/hooks';
import { useAccessControl } from 'shared/access-control';
import {
  getAllUsersFromResponse,
  getInfoText,
  getSurveyTemplateDescriptionSchema,
} from 'shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TemplateDescriptionForm from '../../template/components/template-description-form';
import {
  ConfirmationPopup,
  SURVEYS_PER_PAGE,
  ToasterMessage,
} from './constant';
import ScListActivateButton from './components/sc-list-activate-button';
import { CollectorDataType } from './types';
import EmailProvider from 'shared/email-verify-provider';
import ResponseCount from './components/survey-response-count';
import { useProductConfigContext } from 'shared/product-config-provider';
import { ProductConfig as localConfig } from '../action/constants';
import { GLOBAL_DATE_TIME_FORMAT, limits } from 'shared/constants';
import { SaveSurveyType } from '../action/types';
import EmptyState from 'shared/emptyState';
import { getViewboxFromState } from '../action/collector/utils';

const SurveyList = ({
  goToRoute,
  product,
  hostConfig,
  role,
  setBreadcrumbData,
}: {
  goToRoute: GoToRouteType;
  product?: string;
  role?: string;
  hostConfig: HostConfigType;
  setBreadcrumbData?: SetBreadcrumbDataType;
}) => {
  const query = useQueryParams();
  const history: History = useHistory();
  const [, setToast] = useToasts();
  const paramsProduct =
    query.get('product') || window.localStorage.getItem('product');
  const productCtxt = useProductConfigContext();
  const productConfig = {
    ...productCtxt,
    ...(paramsProduct ? localConfig[paramsProduct] : {}),
  };
  const [isAccountVerified, setIsAccountVerified] =
    React.useState<boolean>(false);
  const [isUserVerified, setIsUserVerified] = React.useState<boolean>(false);
  const [pageToken, setPageToken] = React.useState<any>({ 0: '' });
  const [confirmationData, setConfirmationData] =
    React.useState<ConfirmationType>({});
  const [surveys, setSurveys] = useState<SurveyType[]>();
  const [surveyIdToClone, setSurveyIdToClone] = useState<string>('');
  const [surveyData, setSurveyData] = useState<SurveysResponseType>({});
  const [surveyToClone, setSurveyToClone] = useState<SurveyType>({});
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [pageFilters, setPageFilters] = useState<any>({});
  const [collectorData, setCollectorData] = useState<CollectorDataType[]>([]);
  const [showCloneSurvey, toggleCloneSurvey] = React.useState<boolean>(false);

  const { access, updateRole } = useAccessControl();
  const { data: accountInfo, isLoading: isLoadingAccountInfo } = useAccountInfo(
    {
      refetchOnWindowFocus: !isAccountVerified,
      callback: (isSuccess: boolean, response: UserType) => {
        if (isSuccess && response.verified) {
          setIsAccountVerified(true);
        }
      },
    },
  );
  const { data: userInfo, isLoading: isLoadingUserInfo } = useUserInfo({
    refetchOnWindowFocus: !isUserVerified,
    callback: (isSuccess: boolean, response: UserType) => {
      if (isSuccess && response.verified) {
        setIsUserVerified(true);
      }
    },
  });
  const { data: activeSurveyCount = NaN } = useGetAllActiveSurveysCount({});
  const { data: accountUsers } = useGetAccountUsers({});
  const {
    data: allSurveysCount,
    refetch: refetchAllSurveyCount,
    isError: isAllSurveysCountError,
  } = useGetAllSurveysCount({});

  const allUsers: AllUsersType = React.useMemo(
    () => getAllUsersFromResponse(accountUsers),
    [accountUsers],
  );

  const infoTextBanner = React.useMemo<string>(
    () =>
      productConfig.singleChannelMode
        ? getInfoText({
            userInfo,
            accountInfo,
            activeSurveyCount,
            productConfig,
            hostConfig,
          })
        : getInfoText({
            activeSurveyCount,
            productConfig,
            hostConfig,
          }),
    [
      userInfo,
      accountInfo,
      activeSurveyCount,
      hostConfig?.limits,
      productConfig.singleChannelMode,
    ],
  );

  const [isFilterApplied, setIsFilterApplied] = React.useState(false);

  const {
    isFetching,
    isLoading: isLoadingSurveys,
    refetch: refetchAllSurveys,
  } = useGetAllSurveys({
    pageToken: pageToken[pageIndex] || '',
    pageIndex,
    filters: pageFilters,
    isNewFilter: isFilterApplied,
    callback: async (isSuccess: boolean, response: SurveysResponseType) => {
      if (isSuccess) {
        setSurveyData(response);
        const nextURL = get(response, ['_links', 'next', 'href'], '');
        if (nextURL) {
          const parseData: any = qs.parseUrl(nextURL);
          if (parseData.query?.next_token) {
            setPageToken((pgTkn: any) => ({
              ...pgTkn,
              [pageIndex + 1]: parseData.query.next_token,
            }));
          }
        }
      }
    },
  });

  const { mutate: deleteSurvey } = useDeleteSurvey({
    setToast,
    callback: (isSuccess: boolean, response: any, variables: SurveyType) => {
      if (isSuccess) {
        setTakenNames((old: string[]) =>
          old.filter((oldName) => oldName !== variables?.name),
        );
        setConfirmationData({});
      }
    },
  });

  const { mutate: surveyStateChange, isLoading: isSavingSurveyState } =
    useSurveyStateChange({
      setToast,
      callback: (isSuccess: boolean, _: any, data: SurveyChangeStateType) => {
        const { newState } = data;
        if (isSuccess) {
          setConfirmationData({});
          if (newState === 'activate')
            setToast({
              type: 'success',
              delay: 3000,
              text: ToasterMessage[newState].success,
            });
        } else {
          if (newState === 'activate')
            setToast({
              type: 'error',
              delay: 3000,
              text: ToasterMessage[newState].error,
            });
        }
      },
    });

  const { mutateAsync: collectorStateChange } = useCollectorStateChange({
    callback: (isSuccess: boolean, _: any, data: CollectorStateChangeType) => {
      const { surveyId, newState } = data;
      if (isSuccess) {
        setConfirmationData({});
        if (newState === 'complete' && surveyId) {
          setToast({
            type: 'success',
            delay: 3000,
            text: 'Survey closed successfully',
          });
          getCollectorsInfo(surveyId).then((res: any) => {
            const { surveyCollectors } = res;
            const newCollectorData = collectorData.map(
              (collectorData: CollectorDataType) => {
                if (collectorData.id === surveyId) {
                  return {
                    ...collectorData,
                    collectors: surveyCollectors || [],
                  };
                }
                return collectorData;
              },
            );
            setCollectorData(newCollectorData);
          });
        }
      } else {
        if (newState === 'complete')
          setToast({
            type: 'error',
            delay: 3000,
            text: 'Survey close failed',
          });
      }
    },
  });

  const { isFetching: isFetchingSurvey, refetch: refetchSurvey } = useGetSurvey(
    {
      surveyId: surveyIdToClone || '',
      callback: (isSuccess: boolean, response: SurveyType) => {
        if (isSuccess) {
          setSurveyToClone(response);
        }
      },
    },
  );

  const { mutate: saveSurvey, isLoading: isSavingSurvey } = useSaveSurvey({
    callback: async (
      isSuccess: boolean,
      response: any,
      { payload }: SaveSurveyType,
    ) => {
      if (isSuccess) {
        setToast({
          type: 'success',
          delay: 3000,
          text: 'Survey created successfully',
        });
        setPageToken({ 0: '' });
        setConfirmationData({});
        setSurveyToClone({});
        setSurveyIdToClone('');
        toggleCloneSurvey(false);
        refetchAllSurveyCount();
      } else {
        if (response?.response?.data === 'Duplicate survey name') {
          setTakenNames((old: string[]) =>
            uniq([...old, payload.name || ''].filter((val) => !!val)),
          );
          surveyTemplateDescriptionForm.setError('name', {
            message: `Survey name exists already`,
          });
        } else {
          const name = payload.state === 'DRAFT' ? 'Draft' : 'Survey';
          setToast({
            type: 'error',
            delay: 3000,
            text: payload.id
              ? `${name} save failed`
              : `${name} creation failed`,
          });
        }
      }
    },
  });

  const [takenNames, setTakenNames] = useState<string[]>([]);
  useEffect(() => {
    const surveyNames: string[] = (surveys || [])
      .map((eachSurvey: SurveyType) => eachSurvey.name || '')
      .filter((surveyNames) => surveyNames !== '');
    setTakenNames((old) => uniq([...old, ...surveyNames]));
  }, [surveys]);

  const surveyTemplateDescriptionForm =
    useForm<SurveyTemplateDescriptionFormType>({
      defaultValues: {
        name: '',
        desc: '',
      },
      mode: 'all',
      resolver: yupResolver(
        getSurveyTemplateDescriptionSchema('Survey', takenNames || []),
      ),
    });

  const handleClone = (newValues: SurveyTemplateDescriptionFormType) => {
    const formValues: SurveyType = {
      ...(surveyToClone || {}),
      ...newValues,
    };
    formValues.state = 'DRAFT';
    const { id, ...payload } = formValues;
    saveSurvey({ payload: getSurveyPayload(payload) });
  };

  const handleActions = (
    option: string,
    id: any,
    selectedSurvey: SurveyType,
  ) => {
    switch (option) {
      case 'edit':
        goToRoute({
          path: `/survey/${id}/edit`,
          type: 'SURVEY-EDIT',
          history,
        });
        break;
      case 'delete':
        setConfirmationData({
          onConfirm: () => {
            setConfirmationData((old) => ({
              ...old,
              confirmButtonDisabled: true,
            }));
            deleteSurvey(selectedSurvey);
          },
          ...ConfirmationPopup[option],
        });
        break;
      case 'pause':
        setConfirmationData({
          onConfirm: async () => {
            setConfirmationData((old) => ({
              ...old,
              confirmButtonDisabled: true,
            }));
            surveyStateChange({
              newState: 'pause',
              survey: selectedSurvey,
              surveyId: id,
            });
            if (productConfig?.singleChannelMode) {
              const surveyCollectors: CollectorDataType | undefined =
                collectorData.find((a: any) => a.id === id);
              const emailCollector: CollectorType | undefined =
                surveyCollectors?.collectors?.find(
                  (item: any) => item.channel === 'EMAIL',
                );
              // Collector state changes only for singleChannelMode
              await collectorStateChange({
                surveyId: id,
                collectorId: emailCollector?.id || '',
                newState: 'pause',
              });
            }
          },
          ...ConfirmationPopup[option],
        });
        break;
      case 'activate':
        setConfirmationData({
          onConfirm: async () => {
            setConfirmationData((old) => ({
              ...old,
              confirmButtonDisabled: true,
            }));
            await surveyStateChange({
              newState: 'activate',
              survey: selectedSurvey,
              surveyId: id,
            });
            // This is handled in the ScListActivateButton component
            // if (productConfig?.singleChannelMode) {
            //   const surveyCollectors: any = collectorData.find(
            //     (a: any) => a.id === id,
            //   );
            //   const emailCollector = surveyCollectors?.collectors?.find(
            //     (item: any) => item.channel === 'EMAIL',
            //   );
            //   await collectorStateChange({
            //     surveyId: id,
            //     collectorId: emailCollector.id,
            //     newState: 'activate',
            //   });
            // }
          },
          ...ConfirmationPopup[option],
        });
        break;
      case 'clone':
        if (
          productConfig.enableSurveyLimits &&
          !isNil(allSurveysCount) &&
          // allSurveysCount can be NaN meaning the network call failed
          (isNaN(allSurveysCount) ||
            allSurveysCount >=
              Number(`${hostConfig.limits?.surveys || limits.surveys}`))
        ) {
          setToast({
            type: 'warning',
            delay: 3000,
            text: 'Survey limit reached',
          });
          toggleCloneSurvey(false);
        } else {
          setSurveyIdToClone(selectedSurvey.id || '');
          toggleCloneSurvey(true);
          surveyTemplateDescriptionForm.setValue(
            'name',
            selectedSurvey?.name ? `Copy of ${selectedSurvey.name}` : '',
          );
        }
        break;
      case 'close':
        if (productConfig?.singleChannelMode) {
          setConfirmationData({
            onConfirm: async () => {
              setConfirmationData((old) => ({
                ...old,
                confirmButtonDisabled: true,
              }));

              const surveyCollectors: CollectorDataType | undefined =
                collectorData.find((a: CollectorDataType) => a.id === id);
              const emailCollector = surveyCollectors?.collectors?.find(
                (item: any) => item.channel === 'EMAIL',
              );
              await collectorStateChange({
                surveyId: id,
                collectorId: emailCollector?.id || '',
                newState: 'complete',
              });
            },
            ...ConfirmationPopup[option],
          });
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (surveyIdToClone) refetchSurvey();
  }, [surveyIdToClone]);

  useEffect(() => {
    updateRole(role);
  }, [role]);

  useEffect(() => {
    refetchAllSurveys();
  }, [pageIndex]);

  useEffect(() => {
    refetchAllSurveys();
  }, [pageFilters]);

  useEffect(() => {
    if (surveyData.surveys) {
      const collectorArr = surveyData.surveys.map((a: any) =>
        getCollectorsInfo(a.id),
      );

      Promise.all(collectorArr).then((values: CollectorsResponseType[]) => {
        setCollectorData(
          (surveyData.surveys || []).map((item: SurveyType, index: number) => {
            return {
              id: item.id || '',
              collectors: values[index].surveyCollectors || [],
            };
          }),
        );
      });
      setSurveys(surveyData.surveys);
      if (setBreadcrumbData)
        setBreadcrumbData((old) => ({
          ...old,
          text: `(${Number(
            surveyData?.metadata?.count || surveyData?._links?.count || '0',
          )}/${hostConfig?.limits?.surveys || 0})`,
        }));
    }
  }, [surveyData]);

  const count = Number(
    surveyData?.metadata?.count || surveyData?._links?.count || '0',
  );

  const columns = [
    {
      Header: 'Name',
      accessor: ({ id, name, desc }: SurveyType) => (
        <div className="title">
          <a
            onClick={() =>
              goToRoute({
                path: `/survey/${id}/edit`,
                type: 'SURVEY-EDIT',
                history,
              })
            }
            title={name || ''}
          >
            {name}
          </a>
          <div className="desc" title={desc || ''}>
            {desc}
          </div>
        </div>
      ),
      disableSortBy: true,
    },
    {
      Header: 'Theme',
      accessor: ({ type }: SurveyType) => <Tag> {type}</Tag>,
    },
    {
      ...(productConfig?.singleChannelMode
        ? {
            Header: 'Frequency',
            accessor: ({ id }: SurveyType) => {
              const { collectors } =
                collectorData.find((cData) => cData.id === id) || {};
              const emailCollector = collectors?.find(
                (collector) => collector.channel === 'EMAIL',
              );
              return (
                <>
                  {collectors ? (
                    getSurveyFrequency(emailCollector)
                  ) : (
                    <Skeleton width={30} />
                  )}
                </>
              );
            },
          }
        : {
            Header: 'Active collectors',
            accessor: ({ id }: SurveyType) => {
              const currentItem = collectorData.find(
                (cData: CollectorDataType) => cData.id === id,
              );
              const activeCollectors = (currentItem?.collectors || []).filter(
                (item: CollectorType) => item.state === 'ACTIVE',
              );
              const populated: ChannelCountType =
                getConfiguredCollectors(activeCollectors);
              return (
                <>
                  {currentItem ? (
                    activeCollectors?.length ? (
                      <Tooltip
                        html={
                          <div>
                            {AVAILABLE_SURVEY_COLLECTOR_TYPES.filter(
                              (sColl: SurveyCollectorType) =>
                                !isNil(populated[sColl.id]),
                            ).map((sColl: SurveyCollectorType) => (
                              <p key={sColl.id}>{`${sColl.name}(${
                                populated[sColl.id]
                              })`}</p>
                            ))}
                          </div>
                        }
                        position="right"
                      >
                        <a
                          onClick={() =>
                            goToRoute({
                              path: `/survey/${id}/edit?step=2&tab=collector`,
                              history,
                            })
                          }
                          className="link"
                        >
                          {activeCollectors.length}
                        </a>
                      </Tooltip>
                    ) : (
                      <span>0</span>
                    )
                  ) : (
                    <Skeleton width={30} />
                  )}
                </>
              );
            },
          }),
    },
    {
      Header: 'Created',
      accessor: ({ created_at, meta }: { created_at: string; meta: any }) => {
        const { created_by = '' } = meta || {};
        const userName = `${
          get(allUsers, [created_by, 'first_name'], '') || ''
        } ${get(allUsers, [created_by, 'last_name'], '') || ''}`.trim();
        const userEmail = get(allUsers, [created_by, 'email'], '') || '';

        const createdBy = userName || userEmail || 'Unknown user';

        return (
          <>
            <div className="created-by" title={createdBy}>
              {createdBy}
            </div>
            <div className="created-at">
              {moment(created_at)
                .tz(accountInfo?.timezone || moment.tz.guess())
                .format(GLOBAL_DATE_TIME_FORMAT)}
            </div>
          </>
        );
      },
    },
    {
      Header: 'Responses',
      accessor: ({ id, state }: { id: string; state: string }) => {
        return (
          <ResponseCount surveyId={id} state={state} goToRoute={goToRoute} />
        );
      },
    },
    {
      Header: 'State',
      accessor: ({ id, state }: SurveyType) => {
        const survState = state || 'DRAFT';
        const currentSurveyCollectors: CollectorDataType | undefined =
          collectorData.find((a: CollectorDataType) => a.id === id);
        if (productConfig?.singleChannelMode && survState !== 'DRAFT') {
          const emailCollector: CollectorType | undefined =
            currentSurveyCollectors?.collectors?.find(
              (item: CollectorType) => item.channel === 'EMAIL',
            );
          const consolidated = emailCollector?.state
            ? getConsolidatedState(survState, emailCollector?.state)
            : null;
          const { size, viewBox } = getViewboxFromState(
            consolidated || 'DRAFT',
          );
          return (
            <>
              {currentSurveyCollectors?.collectors?.length && consolidated ? (
                <>
                  <SurveyState statename={consolidated}>
                    <div className="icon">
                      <Icons
                        glyph={consolidated.toLowerCase()}
                        viewBox={viewBox}
                        size={size}
                      />
                    </div>
                    <div>{getSurveyStateNameFromState(consolidated)}</div>
                  </SurveyState>
                  {consolidated === 'SCHEDULED' &&
                    emailCollector?.schedule_info?.start_date && (
                      <div className="td-collector">
                        {
                          <div className="collector-description created-at">
                            {moment(
                              new Date(
                                emailCollector?.schedule_info?.start_date,
                              ),
                            )
                              .tz(accountInfo?.timezone || moment.tz.guess())
                              .format(GLOBAL_DATE_TIME_FORMAT)}
                          </div>
                        }
                      </div>
                    )}
                  {consolidated === 'COMPLETE' &&
                    (emailCollector?.schedule_info?.end_date ||
                      emailCollector?.updated_at) && (
                      <div className="td-collector">
                        <div className="collector-description created-at">
                          {moment(
                            new Date(
                              emailCollector?.schedule_info?.end_date ||
                                emailCollector?.updated_at ||
                                '',
                            ),
                          )
                            .tz(accountInfo?.timezone || moment.tz.guess())
                            .format(GLOBAL_DATE_TIME_FORMAT)}
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <Skeleton width={60} height={24} />
              )}
            </>
          );
        }
        // Only DRAFT surveys and MC mode
        else
          return (
            <SurveyState statename={survState}>
              <div className="icon">
                <Icons
                  glyph={survState.toLowerCase()}
                  viewBox="0 0 12 12"
                  size={12}
                />
              </div>
              <div>{getSurveyStateNameFromState(survState)}</div>
            </SurveyState>
          );
      },
    },
    {
      Header: '  ',
      accessor: (surv: SurveyType) => {
        if (access?.indexOf('survey:write') < 0) return;
        const { state, id } = surv;
        const survState = state || 'DRAFT';
        if (productConfig?.singleChannelMode) {
          const currentSurveyCollectors: CollectorDataType | undefined =
            collectorData.find((a: CollectorDataType) => a.id === id);
          const emailCollector: CollectorType | undefined =
            currentSurveyCollectors?.collectors?.find(
              (item: CollectorType) => item.channel === 'EMAIL',
            );
          const consolidated = emailCollector?.state
            ? getConsolidatedState(survState, emailCollector?.state)
            : null;
          const { actionId, buttonTxt } = getActionButtonTextByState(
            consolidated || 'DRAFT',
          );
          if (actionId === '') return;
          if (actionId === 'activate') {
            return (
              <Flex justifyContent="flex-end">
                <ScListActivateButton
                  survey={surv}
                  activeSurveyCount={activeSurveyCount}
                  setCollectorData={setCollectorData}
                  collectorData={collectorData}
                  hostConfig={hostConfig}
                  userInfo={userInfo || {}}
                  collector={emailCollector || {}}
                  accountInfo={accountInfo || {}}
                />
              </Flex>
            );
          } else {
            return (
              <Flex justifyContent="flex-end">
                <Button
                  className="actionbtn"
                  type="secondary"
                  inline
                  disabled={isSavingSurveyState}
                  size="mini"
                  onClick={() => handleActions(actionId, id, surv)}
                >
                  {buttonTxt}
                </Button>
              </Flex>
            );
          }
        } else {
          const { actionId, buttonTxt } = getActionButtonTextByState(survState);
          return (
            <Flex justifyContent="flex-end">
              <Button
                className="actionbtn"
                type="secondary"
                inline
                disabled={
                  isSavingSurveyState ||
                  (actionId === 'activate' && !!infoTextBanner)
                }
                size="mini"
                onClick={() => handleActions(actionId, id, surv)}
              >
                {buttonTxt}
              </Button>
            </Flex>
          );
        }
      },
    },
  ];

  return (
    <Container>
      {showCloneSurvey && (
        <FormProvider {...surveyTemplateDescriptionForm}>
          <TemplateDescriptionForm
            isLoading={isSavingSurvey || isFetchingSurvey}
            onDismiss={() => {
              setConfirmationData({});
              setSurveyToClone({});
              setSurveyIdToClone('');
              toggleCloneSurvey(false);
            }}
            onSubmit={(val: SurveyTemplateDescriptionFormType) =>
              handleClone(val)
            }
            title="Duplicate survey"
            confirmButtonText="Confirm"
            description="Duplicate this survey with its questions, answer options, branching logic and design"
          />
        </FormProvider>
      )}

      <EmailProvider>
        <Table
          data={surveys || []}
          columns={columns}
          count={count}
          infoComponent={
            !!infoTextBanner &&
            !isLoadingSurveys &&
            !isFetching &&
            !isAllSurveysCountError &&
            (isNaN(activeSurveyCount) || count > activeSurveyCount) ? (
              <InfoComponent>
                <DisplayBox className="disp-box error">
                  <Icons
                    viewBox="0 0 16 16"
                    size={16}
                    fill="#d7461f"
                    glyph="info"
                  />
                  <div className="divider" />
                  <div className="text-content">{infoTextBanner}</div>
                </DisplayBox>
              </InfoComponent>
            ) : undefined
          }
          hideTableOnEmpty
          disableRowSelect
          isLoading={
            (isLoadingSurveys ||
              isLoadingAccountInfo ||
              isLoadingUserInfo ||
              !surveys) &&
            !isAllSurveysCountError
          }
          loaderStyles={{ minHeight: 'calc(100vh - 192px)' }}
          hideFilters
          hideBottomPagination
          isFetching={isFetching}
          pageSize={SURVEYS_PER_PAGE}
          headerComponent={
            <ListHeader
              pageFilters={pageFilters}
              setPageFilters={setPageFilters}
              setPageIndex={setPageIndex}
              setPageToken={setPageToken}
              setIsFilterApplied={setIsFilterApplied}
              isLoading={
                isLoadingSurveys ||
                isLoadingAccountInfo ||
                isLoadingUserInfo ||
                !surveys ||
                isFetching
              }
            />
          }
          emptyComponent={
            isAllSurveysCountError ? (
              <EmptyState
                title="No surveys here!"
                message="Network error"
                imgSrc="empty-state-survey.svg"
              />
            ) : (
              <ListEmptyState
                pageFilters={pageFilters}
                goToRoute={goToRoute}
                isSavingSurveyState={isSavingSurveyState}
              />
            )
          }
          onMenuOptionSelect={(option: string, row: SurveyType) =>
            handleActions(option, row?.id, row)
          }
          enableNextPage={!!surveyData?._links?.next}
          enablePreviousPage={!!surveyData?._links?.prev}
          pageIndex={pageIndex}
          setPageIndex={(pgNo: number) => setPageIndex(pgNo)}
          rowMenuOptions={
            access?.indexOf('survey:write') > -1
              ? (rowData: SurveyType) =>
                  getOptions(
                    rowData,
                    productConfig?.singleChannelMode || false,
                    collectorData,
                  )
              : undefined
          }
        />
      </EmailProvider>
      <Confirmation
        width="540px"
        isOpen={Boolean(confirmationData.id)}
        onDismiss={(type: 'overlay' | 'button') => {
          if (type === 'overlay') return;
          setConfirmationData({});
        }}
        title={confirmationData.title}
        cancelButtonDisabled={confirmationData.cancelButtonDisabled}
        confirmButtonDisabled={confirmationData.confirmButtonDisabled}
        cancelButtonText={confirmationData.cancelButtonText || 'Cancel'}
        confirmButtonText={confirmationData.confirmButtonText || 'Ok'}
        onConfirm={confirmationData.onConfirm}
      >
        <ConfirmationText>{confirmationData.description}</ConfirmationText>
        {confirmationData.glyph && confirmationData.infoText && (
          <DisplayBox className={confirmationData.className}>
            <Icons
              fill={confirmationData.fill}
              viewBox={confirmationData.viewBox || '0 0 16 16'}
              size={confirmationData.size || '16'}
              width={confirmationData.width || '16'}
              height={confirmationData.height || '16'}
              glyph={confirmationData.glyph}
            />
            <div className="divider" />
            <div className="text-content">{confirmationData.infoText}</div>
          </DisplayBox>
        )}
      </Confirmation>
    </Container>
  );
};

export default SurveyList;
